import { useState, type ReactNode } from 'react';
import { FileCode2, MailOpen, ShieldCheck } from 'lucide-react';
import type { PaigeEmailSettings, PaigeEmailSource } from './paige.global';
import { parseSenders, unusableSenders } from './email-words';

interface EmailSettingsProps {
  source: PaigeEmailSource;
  saving: boolean;
  onSave: (settings: Partial<PaigeEmailSettings>) => Promise<void>;
}

/**
 * The email window's settings tab: what of a message the job reads beyond its attachments, and
 * who may send (last, as its box opens below it), each a row with its mark. Each change saves at once, except the sender list, which
 * saves with its button so a half-typed address is never taken for the list.
 */
export function EmailSettings({ source, saving, onSave }: EmailSettingsProps): ReactNode {
  return (
    <div className="paige-email-sheet-settings">
      <Toggle
        icon={<MailOpen size={20} />}
        title="Read the message itself"
        hint="The message itself becomes a document, headed with who sent it and when."
        on={source.readBody}
        disabled={saving}
        onChange={(readBody) => void onSave({ readBody })}
      />
      <Toggle
        icon={<FileCode2 size={20} />}
        title="Read web page attachments"
        hint="Web page (.html) and text attachments become documents too."
        on={source.readHtml}
        disabled={saving}
        onChange={(readHtml) => void onSave({ readHtml })}
      />
      <Senders source={source} saving={saving} onSave={onSave} />
    </div>
  );
}

/**
 * Only listed senders: the switch; on, the box (an address or @domain per line) and Save. Switching
 * on only opens the box (an empty list would mean anyone); the list takes effect with Save.
 */
function Senders({ source, saving, onSave }: EmailSettingsProps): ReactNode {
  const listed = source.allowedSenders;
  const [text, setText] = useState(listed.join('\n'));
  const [open, setOpen] = useState(listed.length > 0);
  const flip = (on: boolean) => {
    setOpen(on);
    if (!on && listed.length > 0) void onSave({ allowedSenders: [] });
  };
  return (
    <div className="paige-email-sheet-senders">
      <Toggle
        icon={<ShieldCheck size={20} />}
        title="Only listed senders"
        hint={sendersHint(listed.length, open)}
        on={open}
        disabled={saving}
        onChange={flip}
      />
      {open && <SenderBox text={text} onText={setText} saving={saving} onSave={onSave} />}
    </div>
  );
}

function sendersHint(listed: number, open: boolean): string {
  if (listed > 0)
    return `Only ${listed === 1 ? 'this sender' : `these ${listed} senders`} can send here.`;
  if (open) return 'Add an address or @domain per line, then Save. Until then anyone can send.';
  return 'Off: anyone can send. Failed sender checks are refused either way.';
}

/** The sender list itself, one per line, saved with its button. */
function SenderBox({
  text,
  onText,
  saving,
  onSave,
}: {
  text: string;
  onText: (text: string) => void;
  saving: boolean;
  onSave: EmailSettingsProps['onSave'];
}): ReactNode {
  const unusable = unusableSenders(text);
  return (
    <>
      <textarea
        rows={3}
        value={text}
        placeholder={'ap@vendor.example\n@vendor.example'}
        aria-label="Allowed senders, one per line"
        disabled={saving}
        onChange={(event) => onText(event.currentTarget.value)}
      />
      <Unusable lines={unusable} />
      <button
        type="button"
        className="paige-email-sheet-button"
        disabled={saving}
        onClick={() => void onSave({ allowedSenders: parseSenders(text) })}
      >
        Save senders
      </button>
    </>
  );
}

function Toggle({
  icon,
  title,
  hint,
  on,
  disabled,
  onChange,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
  on: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}): ReactNode {
  return (
    <label className="paige-email-sheet-switch">
      <span className="paige-email-sheet-row-icon" aria-hidden="true">
        {icon}
      </span>
      <span>
        <strong>{title}</strong>
        <small>{hint}</small>
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={on}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    </label>
  );
}

/** The lines Paige cannot keep, named before Save rather than dropped in silence. */
function Unusable({ lines }: { lines: string[] }): ReactNode {
  if (lines.length === 0) return null;
  return (
    <p className="paige-email-sheet-error" role="status">
      Paige cannot use {lines.join(', ')}: write one address or one whole domain (@vendor.example)
      per line.
    </p>
  );
}
